import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  HStack,
  Skeleton,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';
import { MdDelete, MdDownload, MdEdit, MdSend } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Card from '@/components/ui/Card/Card';
import { Header } from '@/components/ui/Header/Header';
import { Breadcrumbs } from '@/components/ui/Navigation/Breadcrumbs';
import { getDomainNames, useDomainsQuery } from '@/features/domains';
import { createRolesHook } from '@/hooks/useRoles';
import { Breadcrumb } from '@/types/Breadcrumbs';

import { useDeleteSurveyMutation, useGetSurveyQuery, useRepublishSurveyMutation } from '../api';
import { SurveyFieldPreviewList } from '../components/SurveyFieldPreviewList';
import {
  downloadSurveyAnswersReport,
  formatSurveyDate,
  getSurveyStatus,
  getSurveyVisibilityLabel,
  isSurveyPartiallyPublished,
} from '../utils';

const useRoles = createRolesHook(['user', 'admin']);

export const SurveyDetailsPage = () => {
  useRoles();
  const { globalId } = useParams<{ globalId: string }>();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const [deleteSurvey, { isLoading: isDeleting }] = useDeleteSurveyMutation();
  const [republishSurvey, { isLoading: isRepublishing }] = useRepublishSurveyMutation();
  const { data: domains } = useDomainsQuery();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const decodeHtmlEntities = (value: string) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };

  const {
    data: survey,
    isLoading,
    isFetching,
    isError,
  } = useGetSurveyQuery(globalId ?? '', {
    skip: !globalId,
  });

  const breadcrumbs = useMemo<Breadcrumb[]>(() => {
    const items: Breadcrumb[] = [
      {
        path: '/app/surveys',
        displayValue: 'Ankiety',
        isCurrentRoute: false,
      },
    ];
    if (survey?.title) {
      items.push({
        path: `/app/surveys/${survey.globalId}`,
        displayValue: survey.title,
        isCurrentRoute: true,
      });
    } else {
      items.push({
        path: '#',
        displayValue: 'Szczegóły ankiety',
        isCurrentRoute: true,
      });
    }
    return items;
  }, [survey]);

  const handleEdit = () => {
    if (!survey) {
      return;
    }
    navigate(`/app/surveys/${survey.globalId}/edit`);
  };

  const handleRepublish = async () => {
    if (!survey) {
      return;
    }
    try {
      const { failedSchemas = [] } = await republishSurvey(survey.globalId).unwrap();
      if (failedSchemas.length > 0) {
        const failedNames = getDomainNames(failedSchemas, domains);
        toast.warning(`Ankieta nadal nie dotarła do placówek: ${failedNames.join(', ')}.`);
      } else {
        toast.success('Ankieta została opublikowana.');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Nie udało się opublikować ankiety.');
    }
  };

  const handleDownloadReport = async () => {
    if (!survey) {
      return;
    }
    const reportName = (() => {
      const sanitizedTitle = survey.title.replace(/[\\/:*?"<>|]/g, '').trim();
      if (sanitizedTitle) {
        return `${sanitizedTitle}_odpowiedzi`;
      }
      return `ankieta_${survey.globalId}_odpowiedzi`;
    })();
    try {
      setIsGeneratingReport(true);
      await downloadSurveyAnswersReport(survey.globalId, reportName);
      toast.success('Raport XLSX został wygenerowany.');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.data?.message ||
          'Nie udało się pobrać raportu ankiety.'
      );
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleDelete = async () => {
    if (!survey) {
      return;
    }
    try {
      await deleteSurvey(survey.globalId).unwrap();
      toast.success('Ankieta została usunięta.');
      navigate('/app/surveys');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Nie udało się usunąć ankiety.');
    }
  };

  if (!globalId) {
    return (
      <Center py={20}>
        <Alert status="error" variant="left-accent" borderRadius="md" maxW="lg">
          <AlertIcon />
          <AlertDescription>Nieprawidłowy identyfikator ankiety.</AlertDescription>
        </Alert>
      </Center>
    );
  }

  if (isLoading || isFetching) {
    return (
      <Stack spacing={6}>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <Stack spacing={1}>
          <Skeleton height="24px" width="240px" />
          <Skeleton height="16px" width="320px" />
        </Stack>
        <Card>
          <Stack spacing={4}>
            <Skeleton height="20px" width="180px" />
            <Skeleton height="16px" width="60%" />
            <Skeleton height="16px" width="40%" />
          </Stack>
        </Card>
        <Center py={10}>
          <Spinner color="purple.500" />
        </Center>
      </Stack>
    );
  }

  if (isError || !survey) {
    return (
      <Center py={20}>
        <Alert status="error" variant="left-accent" borderRadius="md" maxW="lg">
          <AlertIcon />
          <Stack spacing={1}>
            <AlertTitle>Nie udało się wczytać ankiety</AlertTitle>
            <AlertDescription>Spróbuj ponownie później lub wróć do listy ankiet.</AlertDescription>
          </Stack>
        </Alert>
      </Center>
    );
  }

  const createdAt = dayjs(survey.createdAt).format('DD.MM.YYYY HH:mm');
  const updatedAt = dayjs(survey.updatedAt).format('DD.MM.YYYY HH:mm');
  const startDateLabel = formatSurveyDate(survey.startDate);
  const endDateLabel = formatSurveyDate(survey.endDate);
  const descriptionHtml = survey.description ? decodeHtmlEntities(survey.description) : '';

  return (
    <Stack spacing={6}>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <HStack justify="space-between" align="center">
        <Header pb={0}>{survey.title}</Header>
        <HStack spacing={2}>
          {isSurveyPartiallyPublished(survey) ? (
            <Button leftIcon={<MdSend />} onClick={handleRepublish} isLoading={isRepublishing}>
              Opublikuj ponownie
            </Button>
          ) : null}
          {survey.isPublished || isSurveyPartiallyPublished(survey) ? (
            <Button
              colorScheme="purple"
              variant="solid"
              leftIcon={<MdDownload />}
              onClick={handleDownloadReport}
              isLoading={isGeneratingReport}
            >
              Pobierz raport XLSX
            </Button>
          ) : (
            <Button leftIcon={<MdEdit />} onClick={handleEdit}>
              Edytuj
            </Button>
          )}
          <Button colorScheme="red" leftIcon={<MdDelete />} onClick={onOpen}>
            Usuń
          </Button>
        </HStack>
      </HStack>

      <Card>
        <Stack spacing={4} color="gray.800">
          <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align="flex-start">
            <Badge colorScheme={getSurveyStatus(survey).colorScheme} variant="subtle">
              {getSurveyStatus(survey).label}
            </Badge>
            <Badge variant="outline">{getSurveyVisibilityLabel(survey.visibilityScope)}</Badge>
          </Stack>
          <Stack spacing={2} fontSize="sm">
            <Text>
              <Text as="span" fontWeight="semibold">
                Data startu:
              </Text>{' '}
              {startDateLabel}
            </Text>
            <Text>
              <Text as="span" fontWeight="semibold">
                Data zakończenia:
              </Text>{' '}
              {endDateLabel}
            </Text>
            <Text>
              <Text as="span" fontWeight="semibold">
                Utworzono:
              </Text>{' '}
              {createdAt}
            </Text>
            <Text>
              <Text as="span" fontWeight="semibold">
                Ostatnia aktualizacja:
              </Text>{' '}
              {updatedAt}
            </Text>
          </Stack>
          <Divider />
          <Box>
            <Text fontWeight="semibold" mb={1}>
              Domeny publikacji
            </Text>
            {survey.schemas && survey.schemas.length > 0 ? (
              <HStack spacing={2} wrap="wrap">
                {survey.schemas.map((schema) => (
                  <Badge key={schema} colorScheme="purple" variant="subtle">
                    {schema}
                  </Badge>
                ))}
              </HStack>
            ) : (
              <Text fontSize="sm" color="gray.600">
                Wszystkie domeny
              </Text>
            )}
          </Box>
          <Divider />
          <Stack spacing={3}>
            <Text fontWeight="semibold">Opis ankiety</Text>
            {descriptionHtml ? (
              <Box
                fontSize="sm"
                color="gray.700"
                sx={{
                  '& p': { mb: 2 },
                  '& ul': { pl: 5, mb: 2 },
                  '& ol': { pl: 5, mb: 2 },
                }}
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            ) : (
              <Text fontSize="sm" color="gray.600">
                Brak opisu.
              </Text>
            )}
          </Stack>
        </Stack>
      </Card>

      <Card>
        <Stack spacing={4}>
          <Text fontWeight="semibold" color="gray.800">
            Pytania i elementy ankiety
          </Text>
          <SurveyFieldPreviewList fields={survey.fields ?? []} />
        </Stack>
      </Card>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="semibold">
              Usuń ankietę
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text>
                Czy na pewno chcesz usunąć ankietę{' '}
                <Text as="span" fontWeight="semibold">
                  {survey.title}
                </Text>
                ? Tego działania nie można cofnąć.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Anuluj
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={isDeleting}>
                Usuń
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Stack>
  );
};
